
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Link } from "react-router-dom";

import {
  Activity,
  AlertCircle,
  ArrowRight,
  Flame,
  FolderTree,
  HeartPulse,
  Smartphone,
  TrendingUp,
  BadgeCheck,
  CheckCircle2,
  Eye,
  Heart,
  MapPin,
  MousePointerClick,
  RefreshCw,
  Users,
} from "lucide-react";

import UserAvatar from "../../components/UserAvatar";

import {
  API,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Skeleton,
  StatusBadge,
  apiFetch,
  cx,
} from "./_shared";

import {
  BarList,
  ChartCard,
  ColumnChart,
  KpiCard,
  Legend,
  LineChart,
  VIZ,
  fmtNum,
} from "../../components/admin/charts";

import {
  DonutChart,
  ProgressRing,
} from "../../components/admin/chartsExtra";

import {
  GRANULARITY_UNIT,
  formatBucket,
  formatFullDate,
  rangeText,
} from "../../components/admin/timeSeries";

import TrafficRangePicker, {
  DEFAULT_RANGE,
  rangeQuery,
} from "../../components/admin/TrafficRangePicker";

// =============================================
// CONSTANTS & HELPERS
// =============================================

const DEVICE_LABEL = {
  mobile: "Điện thoại",
  desktop: "Máy tính",
  tablet: "Máy tính bảng",
};

function timeAgo(iso) {
  const t = new Date(iso).getTime();

  if (!Number.isFinite(t)) return "";

  const mins = Math.max(
    0,
    Math.round((Date.now() - t) / 60000)
  );

  if (mins < 1) return "Vừa xong";
  if (mins < 60) return `${mins} phút trước`;

  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} giờ trước`;

  const days = Math.round(hours / 24);
  if (days < 30) return `${days} ngày trước`;

  return new Date(iso).toLocaleDateString("vi-VN");
}

function formatMonth(key, long) {
  const [y, m] = String(key).split("-");

  if (!y) return key;

  return long
    ? `Tháng ${Number(m)}/${y}`
    : `T${Number(m)}`;
}

const sum = (rows, key) =>
  rows.reduce(
    (a, r) => a + (Number(r[key]) || 0),
    0
  );

const pct = (cur, prev) =>
  prev > 0
    ? ((cur - prev) / prev) * 100
    : cur > 0
      ? 100
      : 0;

// =============================================
// DASHBOARD SKELETON
// =============================================

const DashboardSkeleton = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <Skeleton
          key={i}
          className="h-[150px] rounded-xl"
        />
      ))}
    </div>

    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <Skeleton className="h-[380px] rounded-xl xl:col-span-2" />
      <Skeleton className="h-[380px] rounded-xl" />
    </div>

    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <Skeleton className="h-[320px] rounded-xl xl:col-span-2" />
      <Skeleton className="h-[320px] rounded-xl" />
    </div>
  </div>
);

// =============================================
// CONTENT HEALTH CARD
// =============================================

function ContentHealthCard({ health }) {
  const items = (health?.items || []).filter(
    (i) => i.count > 0
  );

  const score = health?.score ?? 0;

  const label =
    score >= 80
      ? "Rất tốt"
      : score >= 50
        ? "Cần cải thiện"
        : "Cần chú ý";

  return (
    <ChartCard
      icon={HeartPulse}
      tone="emerald"
      title="Sức khoẻ nội dung"
      subtitle="Mức độ đầy đủ thông tin của các địa điểm"
      action={
        <Link
          to="/admin/locations"
          className="inline-flex items-center gap-1 text-xs font-semibold text-admin-primary hover:underline"
        >
          Chỉnh sửa
          <ArrowRight size={13} />
        </Link>
      }
    >
      <div className="flex items-center gap-5">
        <ProgressRing
          value={score}
          label="Điểm nội dung"
        />

        <div className="min-w-0">
          <p className="text-sm font-semibold text-admin-text">
            {label}
          </p>

          <p className="mt-1 text-xs leading-relaxed text-admin-textMuted">
            {items.length
              ? `${items.length} mục cần hoàn thiện trên ${fmtNum(health.total)} địa điểm.`
              : `Cả ${fmtNum(health.total)} địa điểm đều đầy đủ thông tin.`}
          </p>
        </div>
      </div>

      {items.length ? (
        <ul className="mt-5 space-y-3.5">
          {items.map((it) => (
            <li key={it.key}>
              <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
                <span
                  className="min-w-0 truncate text-admin-text"
                  title={it.hint}
                >
                  {it.label}
                </span>

                <span className="shrink-0 text-xs font-semibold tabular-nums text-admin-text">
                  {it.count}
                  <span className="font-normal text-admin-textMuted">
                    {" "}/ {it.total}
                  </span>
                </span>
              </div>

              <div className="h-1.5 overflow-hidden rounded-full bg-admin-muted">
                <div
                  className="h-full rounded-full bg-amber-500"
                  style={{
                    width: `${Math.max(
                      4,
                      (it.count /
                        Math.max(1, it.total)) *
                        100
                    )}%`,
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-5 flex items-center gap-2 text-sm text-emerald-700">
          <CheckCircle2 size={16} />
          Không còn việc cần làm.
        </p>
      )}
    </ChartCard>
  );
}

// =============================================
// ADMIN DASHBOARD
// =============================================

const AdminDashboard = () => {
  // -----------------------------
  // STATE
  // -----------------------------

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [range, setRange] = useState(DEFAULT_RANGE);

  const [traffic, setTraffic] = useState(null);
  const [trafficLoading, setTrafficLoading] =
    useState(true);

  // -----------------------------
  // LOAD DASHBOARD
  // -----------------------------

  const load = useCallback(
    async ({ fresh = false } = {}) => {
      setError("");

      try {
        const response = await apiFetch(
          `${API}/analytics/admin/dashboard${
            fresh ? "?refresh=1" : ""
          }`
        );

        setData(response);
      } catch (e) {
        setError(
          e?.message ||
            "Không thể tải số liệu dashboard"
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  // -----------------------------
  // LOAD TRAFFIC SERIES
  // -----------------------------

  useEffect(() => {
    let alive = true;

    setTrafficLoading(true);

    apiFetch(
      `${API}/analytics/admin/traffic/series?${rangeQuery(range)}`
    )
      .then((res) => {
        if (alive) {
          setTraffic(res);
        }
      })
      .catch(() => {
        if (alive) {
          setTraffic(null);
        }
      })
      .finally(() => {
        if (alive) {
          setTrafficLoading(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [range]);

  // -----------------------------
  // PREPARE DASHBOARD DATA
  // -----------------------------

  const view = useMemo(() => {
    const t = data?.trends || {};

    const daily = t.daily || [];
    const monthly = t.monthly || [];
    const kpi = t.kpi || {};

    const curMonth =
      monthly[monthly.length - 1]?.users || 0;

    const prevMonth =
      monthly[monthly.length - 2]?.users || 0;

    return {
      monthly,

      last30: daily.slice(-30),

      kpi: {
        ...kpi,
        newUsersDelta: pct(
          curMonth,
          prevMonth
        ),
        curMonthUsers: curMonth,
      },

      devices: (
        data?.audience?.devices || []
      ).map((d) => ({
        label:
          DEVICE_LABEL[d.label] || d.label,
        value: d.count,
      })),
    };
  }, [data]);

  const counts = data?.counts || {};
  const recent = data?.recentLocations || [];

  const points = traffic?.series || [];
  const g = traffic?.granularity || "day";

  const fmtX = (k, long) =>
    formatBucket(k, g, long);

  // -----------------------------
  // CHART SERIES
  // -----------------------------

  const trafficSeries = [
    {
      label: "Lượt xem trang",
      values: points.map(
        (d) => d.pageviews
      ),
      flags: points.map((d) =>
        d.pageviewsEstimated
          ? "ước tính"
          : null
      ),
      color: VIZ[0],
    },
    {
      label: "Khách truy cập",
      values: points.map(
        (d) => d.visitors
      ),
      color: VIZ[1],
    },
  ];

  const growthSeries = [
    {
      label: "Người dùng mới",
      values: view.monthly.map(
        (m) => m.users
      ),
      color: VIZ[0],
    },
    {
      label: "Địa điểm mới",
      values: view.monthly.map(
        (m) => m.locations
      ),
      color: VIZ[1],
    },
  ];

  // =============================================
  // RENDER
  // =============================================

  return (
    <div className="space-y-6 pb-4">
      {loading ? (
        <DashboardSkeleton />
      ) : error && !data ? (
        <Card>
          <EmptyState
            icon={AlertCircle}
            title="Không tải được dashboard"
            description={error}
            action={
              <Button
                variant="primary"
                icon={RefreshCw}
                onClick={() =>
                  load({ fresh: true })
                }
              >
                Thử lại
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          {/* ===================================
              KPI CARDS
             =================================== */}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              tone="blue"
              icon={MousePointerClick}
              label="Lượt xem trang · 30 ngày"
              value={
                view.kpi.pageviews == null
                  ? "—"
                  : fmtNum(view.kpi.pageviews)
              }
              delta={
                view.kpi.pageviewsDelta ??
                undefined
              }
              deltaLabel="so với 30 ngày trước"
              trend={view.last30.map(
                (d) => d.pageviews
              )}
            />

            <KpiCard
              tone="violet"
              icon={Users}
              label="Khách truy cập · 30 ngày"
              value={fmtNum(
                view.kpi.visitors
              )}
              delta={
                view.kpi.visitorsDelta
              }
              deltaLabel="so với 30 ngày trước"
              trend={view.last30.map(
                (d) => d.visitors
              )}
            />

            <KpiCard
              tone="amber"
              icon={Eye}
              label="Lượt xem địa điểm · 30 ngày"
              value={fmtNum(
                view.kpi.locationViews
              )}
              delta={
                view.kpi.locationViewsDelta
              }
              deltaLabel="so với 30 ngày trước"
              hint={`${fmtNum(
                counts.locationViews
              )} lượt từ trước đến nay`}
              trend={view.last30.map(
                (d) => d.locationViews
              )}
            />

            <KpiCard
              tone="rose"
              icon={Heart}
              label="Người dùng"
              value={fmtNum(
                counts.users
              )}
              delta={
                view.kpi.newUsersDelta
              }
              deltaLabel="người dùng mới so với tháng trước"
              hint={`+${fmtNum(
                view.kpi.curMonthUsers
              )} tháng này · ${fmtNum(
                counts.favorites
              )} lượt yêu thích`}
              trend={view.monthly.map(
                (m) => m.users
              )}
            />
          </div>

          {/* ===================================
              TRAFFIC & CONTENT HEALTH
             =================================== */}

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <ChartCard
              className="xl:col-span-2"
              icon={Activity}
              tone="blue"
              title="Lưu lượng truy cập"
              subtitle={
                traffic
                  ? `${rangeText(
                      traffic.from,
                      traffic.to
                    )} · theo ${
                      GRANULARITY_UNIT[g]
                    } · ${fmtNum(
                      sum(
                        points,
                        "pageviews"
                      )
                    )} lượt xem trang`
                  : "Đang tải…"
              }
              action={
                <TrafficRangePicker
                  value={range}
                  onChange={setRange}
                />
              }
              legend={
                <Legend
                  items={trafficSeries}
                />
              }
              table={{
                columns: [
                  g === "month"
                    ? "Tháng"
                    : g === "week"
                      ? "Tuần"
                      : "Ngày",
                  "Lượt xem trang",
                  "Khách truy cập",
                ],
                rows: points.map((d) => [
                  fmtX(d.key, true),
                  d.pageviews == null
                    ? "—"
                    : fmtNum(
                        d.pageviews
                      ),
                  fmtNum(d.visitors),
                ]),
              }}
            >
              <div
                className={cx(
                  "transition-opacity",
                  trafficLoading &&
                    "opacity-50"
                )}
              >
                {points.length ? (
                  <LineChart
                    labels={points.map(
                      (d) => d.key
                    )}
                    series={trafficSeries}
                    height={280}
                    formatLabel={fmtX}
                  />
                ) : (
                  <Skeleton className="h-[280px] w-full" />
                )}
              </div>

              {traffic?.pageviewsEstimatedUntil ? (
                <p className="mt-3 text-xs text-admin-textMuted">
                  Lượt xem trang đến hết{" "}
                  {formatFullDate(
                    traffic.pageviewsEstimatedUntil
                  )}{" "}
                  là số{" "}
                  <span className="font-semibold text-admin-text">
                    ước tính
                  </span>{" "}
                  (dữ liệu gốc đã mất, tính từ
                  số phiên thật × tỷ lệ lượt
                  xem/phiên). Từ ngày sau đó là
                  số đo thật.
                </p>
              ) : traffic?.pageviewsSince ? (
                <p className="mt-3 text-xs text-admin-textMuted">
                  Lượt xem trang được lưu từ{" "}
                  {formatFullDate(
                    traffic.pageviewsSince
                  )}
                  ; khách truy cập có dữ liệu
                  từ{" "}
                  {formatFullDate(
                    traffic.from
                  )}
                  .
                </p>
              ) : null}
            </ChartCard>

            {data?.health ? (
              <ContentHealthCard
                health={data.health}
              />
            ) : null}
          </div>

          {/* ===================================
              MONTHLY GROWTH & DEVICES
             =================================== */}

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <ChartCard
              className="xl:col-span-2"
              icon={TrendingUp}
              tone="violet"
              title="Tăng trưởng theo tháng"
              subtitle={
                view.monthly.length
                  ? `Người dùng và địa điểm mới · từ ${formatMonth(
                      view.monthly[0].month,
                      true
                    ).toLowerCase()} tới nay`
                  : undefined
              }
              legend={
                <Legend
                  items={growthSeries}
                  shape="rect"
                />
              }
              table={{
                columns: [
                  "Tháng",
                  "Người dùng mới",
                  "Địa điểm mới",
                ],
                rows: view.monthly.map(
                  (m) => [
                    formatMonth(
                      m.month,
                      true
                    ),
                    fmtNum(m.users),
                    fmtNum(m.locations),
                  ]
                ),
              }}
            >
              <ColumnChart
                labels={view.monthly.map(
                  (m) => m.month
                )}
                series={growthSeries}
                height={340}
                formatLabel={formatMonth}
              />
            </ChartCard>

            <ChartCard
              icon={Smartphone}
              tone="cyan"
              title="Khách dùng thiết bị gì"
              subtitle="30 ngày gần nhất"
              action={
                <Link
                  to="/admin/overview"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-admin-primary hover:underline"
                >
                  Chi tiết
                  <ArrowRight size={13} />
                </Link>
              }
            >
              <DonutChart
                parts={view.devices}
                centerLabel="Phiên"
                size={148}
              />

              {data?.audience?.os?.length ? (
                <div className="mt-5 border-t border-admin-border pt-4">
                  <p className="mb-3 text-xs font-medium text-admin-textMuted">
                    Hệ điều hành
                  </p>

                  <BarList
                    items={data.audience.os
                      .slice(0, 4)
                      .map((o) => ({
                        key: o.label,
                        label: o.label,
                        value: o.count,
                      }))}
                  />
                </div>
              ) : null}
            </ChartCard>
          </div>

          {/* ===================================
              TOP LOCATIONS & USERS
             =================================== */}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <ChartCard
              icon={Flame}
              tone="amber"
              title="Địa điểm xem nhiều nhất"
              subtitle="Tổng lượt xem"
            >
              <BarList
                items={(
                  data?.topLocations || []
                ).map((l) => ({
                  key: l._id,
                  label: l.name,
                  value:
                    l.viewCount || 0,
                }))}
                empty="Chưa có lượt xem"
              />
            </ChartCard>

            <ChartCard
              icon={FolderTree}
              tone="emerald"
              title="Địa điểm theo danh mục"
              subtitle={`${fmtNum(
                counts.categories
              )} danh mục · ${fmtNum(
                counts.locations
              )} địa điểm`}
            >
              <BarList
                items={(
                  data?.categoryBreakdown ||
                  []
                ).map((c) => ({
                  key: c.name,
                  label: c.name,
                  value: c.count,
                  suffix: `${c.pct}%`,
                }))}
                empty="Chưa có dữ liệu phân loại"
              />
            </ChartCard>

            {/* RECENT USERS */}

            <Card className="flex flex-col overflow-hidden">
              <CardHeader
                title="Người dùng mới"
                subtitle={`${fmtNum(
                  counts.verifiedUsers
                )}/${fmtNum(
                  counts.users
                )} đã xác minh email`}
                action={
                  <Link
                    to="/admin/users"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-admin-primary hover:underline"
                  >
                    Xem tất cả
                    <ArrowRight size={13} />
                  </Link>
                }
              />

              <ul className="mt-4 flex-1 divide-y divide-admin-border border-t border-admin-border">
                {(
                  data?.recentUsers || []
                ).length === 0 ? (
                  <li>
                    <EmptyState
                      icon={Users}
                      title="Chưa có người dùng"
                    />
                  </li>
                ) : (
                  data.recentUsers.map(
                    (u) => (
                      <li
                        key={u._id}
                        className="flex items-center gap-3 px-5 py-3"
                      >
                        <UserAvatar
                          url={u.avatar}
                          name={
                            u.fullName ||
                            u.email
                          }
                          sizeClass="h-9 w-9"
                          textClass="text-xs font-semibold"
                          roundedClass="rounded-full"
                          className="shrink-0 bg-admin-primaryLight"
                          imgClassName="object-cover"
                        />

                        <div className="min-w-0 flex-1">
                          <p className="flex items-center gap-1 truncate text-sm font-medium text-admin-text">
                            <span className="truncate">
                              {u.fullName ||
                                "—"}
                            </span>

                            {u.emailVerified ? (
                              <BadgeCheck
                                size={14}
                                className="shrink-0 text-sky-600"
                                aria-label="Đã xác minh"
                              />
                            ) : null}
                          </p>

                          <p className="truncate text-xs text-admin-textMuted">
                            {timeAgo(
                              u.createdAt
                            )}
                          </p>
                        </div>

                        <Badge
                          tone={
                            u.authProvider ===
                            "google"
                              ? "info"
                              : "neutral"
                          }
                        >
                          {u.authProvider ===
                          "google"
                            ? "Google"
                            : "Email"}
                        </Badge>
                      </li>
                    )
                  )
                )}
              </ul>
            </Card>
          </div>

          {/* ===================================
              RECENT LOCATIONS
             =================================== */}

          <Card className="overflow-hidden">
            <CardHeader
              title="Địa điểm mới thêm"
              subtitle="8 địa điểm gần nhất"
              action={
                <Link
                  to="/admin/locations"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-admin-primary hover:underline"
                >
                  Xem tất cả
                  <ArrowRight size={13} />
                </Link>
              }
            />

            {recent.length === 0 ? (
              <EmptyState
                icon={MapPin}
                title="Chưa có địa điểm"
                description="Thêm địa điểm đầu tiên để bắt đầu."
              />
            ) : (
              <div className="-mb-px mt-4 grid grid-cols-1 border-t border-admin-border md:grid-cols-2">
                {recent.map(
                  (loc, i) => (
                    <Link
                      key={loc._id}
                      to="/admin/locations"
                      className={`flex items-center gap-3 border-b border-admin-border px-5 py-3 transition hover:bg-admin-muted/60 ${
                        i % 2 === 0
                          ? "md:border-r"
                          : ""
                      }`}
                    >
                      {loc.image?.url ? (
                        <img
                          src={
                            loc.image.url
                          }
                          alt=""
                          loading="lazy"
                          className="h-10 w-10 shrink-0 rounded-lg object-cover"
                          onError={(e) => {
                            e.currentTarget.style.visibility =
                              "hidden";
                          }}
                        />
                      ) : (
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-admin-muted text-admin-textMuted">
                          <MapPin
                            size={16}
                          />
                        </span>
                      )}

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-admin-text">
                          {loc.name}
                        </p>

                        <p className="truncate text-xs text-admin-textMuted">
                          {loc.categoryName ||
                            "Chưa phân loại"}{" "}
                          ·{" "}
                          {timeAgo(
                            loc.createdAt
                          )}
                        </p>
                      </div>

                      <StatusBadge
                        active={
                          loc.isActive
                        }
                      />
                    </Link>
                  )
                )}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
